import { globals } from 'config/globals';
import ComboCounter from 'utils/ComboCounter';
import { device } from 'utils/device';
import HomeColours from 'app/views/home/config/HomeColours';
import ParticleGridTargets from 'app/views/home/webgl/particles/targets/ParticleGridTargets';
import ParallaxItemTargets from 'app/views/home/targets/ParallaxItemTargets';
import {
	BEHAVIOUR_BREATHING,
	BEHAVIOUR_DISABLE_TIMELINE_X,
	FLAG_ACTIVE
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import DepartmentColours from 'app/views/home/webgl/particlesOptimised/departments/DepartmentColours';
import { PhaseRunner } from 'app/views/home/webgl/particlesOptimised/departments/PhaseRunner';
import { SystemsContext } from 'app/views/home/webgl/particlesOptimised/departments/systems/SystemsContext';
import { LinesPhase } from 'app/views/home/webgl/particlesOptimised/departments/systems/phases/LinesPhase';
import { createSystemsPhaseEntries } from 'app/views/home/webgl/particlesOptimised/departments/systems/systemsPhaseEntries';
import { systemsGridConfig } from 'app/views/home/webgl/particlesOptimised/departments/systems/systemsGridConfig';

import type { ParticleCore } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import type { DepartmentDeps } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';
import type { DepartmentScene } from 'app/views/home/webgl/webgl.types';

/**
 * SystemsDepartment (particles-optimised) — FORK of
 * webgl/particles/departments/SystemsDepartment.ts + SystemsParticleProcessor
 * + fields/gridItems/SingleLine.ts + the ParticleTrailEmitter/ParticleTrail
 * behaviours, restructured onto the phase framework (phases.types.ts /
 * PhaseRunner).
 *
 * PHASE-COUNT JUDGMENT (documented): the reference stage table is
 * [SingleLine x10000 frames] — ONE stage looping onto itself, so the runner
 * config is a single LinesPhase entry (departments/systems/phases/). The
 * in-stage configuration cycles (the 8-entry line-mode table, the ~3s marker
 * colour rotation) are NOT phases: they mutate globals that persistent
 * in-flight lines consume on their next retarget, with no arm/teardown —
 * see LinesPhase's header. The wrap every 10000 counter ticks maps to the
 * runner re-running start() (the reference's re-arm over surviving lines);
 * the Rain/BasicGrid dispatch arms are unreachable (dead,
 * documented-not-ported).
 *
 * REUSED as-is: ParticleGridTargets (the dot grid — already carries the
 * allocation-free quick-wins treatment; the facade wires its guide container
 * and resize; this department feeds it the systems/systemsGridConfig.ts
 * set-point via configure() at construction — values == the module defaults,
 * so the grid matches the reference until that file is edited),
 * ParticleGridPatterns, ParticleTargets, ParallaxItemTargets, ComboCounter,
 * HomeColours and the gsap fastModeScale tween.
 *
 * Counter time-domain note: the reference systems stage counter is a PLAIN
 * increment (`_spacesStageCounter++`, NOT deltaTime-normalised — unlike
 * spaces), so the runner is fed tick(1) rather than tick(deltaTime).
 *
 * This shell keeps (department-level):
 *  - the EVERY-FRAME passive pass — the facade mirrors HomeField.tick's
 *    `if (!systems.isActive) systems.tick()` guard, so grid tracking /
 *    container positioning / the boot colour pass run while other scenes
 *    are active (and the reference's brief double-tick window during the
 *    start beat is reproduced);
 *  - scene lifecycle: the 50/100/120ms start beats (setSystemsStart sweep,
 *    phase arm, container reveal), the stop sweep (hard-park at rel {2,2},
 *    alpha zero, free-list wipe) — runner.stop() maps the stop to hide() +
 *    reset() on LinesPhase (lines cleared there);
 *  - the resize stop + 500ms-debounced restart;
 *  - physics onTargetReached routing into LinesPhase (head lerps).
 *
 * Reference quirks kept:
 *  - startAsActiveScene's grid pass centres on device.innerWidth / 2 and
 *    multiplies the sprite scale in; the per-tick pass centres on
 *    innerHeight / 2 with unscaled dims (both verbatim);
 *  - the boot-time first-colour pass forwards [globals.accentColour] while
 *    it is still undefined — passed through verbatim;
 *  - stopAsActiveScene's console.log is dropped (fork convention);
 *  - the reference pool is getParticlePool's off-by-one slice; this fork
 *    uses the full range (stage-5 convention).
 */
export default class SystemsDepartment implements DepartmentScene {
	private _deps: DepartmentDeps;
	private _core: ParticleCore;
	private _ctx: SystemsContext;
	private _colours: DepartmentColours;
	private _runner: PhaseRunner;
	private _linesPhase: LinesPhase;

	private _active: boolean = false;

	private _counters: ComboCounter = new ComboCounter();
	private _resizeTimeout: ReturnType<typeof setTimeout> = null;

	private _firstColour: string;
	private _firstColourSet = false;

	constructor(deps: DepartmentDeps) {
		this._deps = deps;
		this._core = deps.core;
		this._ctx = new SystemsContext(deps.core, deps);
		this._colours = new DepartmentColours(deps.core, deps.scripts, deps);

		//The dot-grid set-point (systems/systemsGridConfig.ts) — values ==
		//the module defaults today, so the grid stays pixel-identical to the
		//reference; this is WHERE a systems grid tweak gets made. Runs before
		//any tick, so the first createGrid() already sees it:
		ParticleGridTargets.configure(systemsGridConfig);

		this._deps.stage.systemsContainer.visible = false;

		this._firstColour = HomeColours.getRandomColour(HomeColours.greenBlues);

		this._linesPhase = new LinesPhase(this._ctx, this._colours, () => this._firstColour);

		//The ordered phase list + timings live in systemsPhaseEntries.ts
		//(single entry — see the class header's phase-count note). No
		//transition hooks: the reference _nextSystemsStage is a bare
		//dispatch with no preamble/analytics:
		this._runner = new PhaseRunner(createSystemsPhaseEntries(this._linesPhase));
		this._runner.initAll();
	}

	public get isActive(): boolean {
		return this._active;
	}

	//DepartmentScene surface
	//-----------------------------------------------------------------------------------------------

	/** Ticks EVERY frame — see the class header's passive-pass note. */
	public tick(): void {
		this._positionParticleContainer();
		this._generateBrokenGrid();

		if (!this._firstColourSet) {
			this._firstColourSet = this._colours.changeSystemsColour([globals.accentColour], true);
		}

		if (!this._active) return;

		this._counters.tick();

		//The stage counter is a plain increment in the reference (see the
		//class header's time-domain note), then the phase's own frame work:
		this._runner.tick(1);
		this._runner.phase.tick();
	}

	/** User is far enough right to be on the systems scene. */
	public startAsActiveScene(): void {
		globals.accentColour = this._firstColour;
		this._colours.changeSystemsColour([globals.accentColour], true);

		const gridSprite: PIXI.Sprite = ParallaxItemTargets.get('systems-grid');

		//Undefined when the systems-grid item hasn't sprite-bound yet — skip
		//the grid-target pass, keep the rest:
		if (gridSprite) {
			const w = gridSprite.width * gridSprite.scale.x;
			const h = gridSprite.height * gridSprite.scale.y;

			ParticleGridTargets.tick(gridSprite.x, device.innerWidth / 2, w, h, this._deps.context.progress);
		}

		this._counters.clear();

		this._counters.add(() => {
			//(globals.skipNormalParticleUpdates hack not ported.)
			for (let n = 0; n < this._ctx.range.count; n++) {
				this._ctx.setSystemsStart(this._ctx.range.start + n);
			}
		}, 50);

		this._counters.add(() => {
			//_nextSystemsStage — arm the single phase, counter from 0:
			this._runner.startPhase(0);
			this._runner.setCounter(0);
		}, 100);

		this._counters.add(() => {
			this._deps.stage.systemsContainer.visible = true;
		}, 120);

		this._active = true;
	}

	public stopAsActiveScene(): void {
		this._counters.clear();

		this._firstColourSet = false;
		this._active = false;
		this._deps.stage.systemsContainer.visible = false;

		//_clearAllLines lives in LinesPhase.reset() (runner.stop = hide +
		//reset on the current phase):
		this._runner.stop();

		const core = this._core;
		for (let i = this._ctx.range.start; i < this._ctx.range.end; i++) {
			//position.set({x: 2, y: 2}, true):
			this._ctx.setHardPosition(i, 2, 2);
			//deactivateAllBehaviours(false) + setVisible(false) + active=false:
			core.behaviourMask[i] &= BEHAVIOUR_BREATHING | BEHAVIOUR_DISABLE_TIMELINE_X;
			core.trailOn[i] = 0;
			core.tAlpha[i] = core.alpha[i] = 0;
			core.stateFlags[i] &= ~FLAG_ACTIVE;
		}

		//pooled = false across the pool:
		this._ctx.freeAll();
	}

	public startSceneDrag(): void {}

	public endSceneDrag(): void {}

	/** Reference resize: full stop, then a 500ms-debounced restart. */
	public resize(): void {
		this.stopAsActiveScene();

		if (this._resizeTimeout) {
			clearTimeout(this._resizeTimeout);
		}

		this._resizeTimeout = setTimeout(() => {
			this.startAsActiveScene();
			this._resizeTimeout = null;
		}, 500);
	}

	/** NEVER routed in the reference: HomeField.setupForWork sends SYSTEMS
	 *  (like every non-screens department) to the SPACES department's no-op.
	 *  Kept as the contract no-op. */
	public setupForWork(): void {}

	/** Reference SystemsDepartment.showFromWork, verbatim: full stop + full
	 *  restart. HomeField.showFromWork's else branch routes SYSTEMS (and any
	 *  unexpected department) here. */
	public showFromWork(): void {
		this.stopAsActiveScene();
		this.startAsActiveScene();
	}

	/** DEAD in the reference (no callers — the live work re-entry is
	 *  showFromWork; the gsap container tween never runs). */
	public fromWorkToHome(): void {}

	/** Facade destroy() hook (no reference equivalent — ParticlesWebGL has
	 *  no destroy): clear the debounced resize restart so it cannot fire
	 *  into a torn-down scene. */
	public destroy(): void {
		if (this._resizeTimeout) {
			clearTimeout(this._resizeTimeout);
			this._resizeTimeout = null;
		}
		this._counters.clear();
	}

	/** The facade routes lerp completions (heads are the only systems lanes
	 *  that lerp) into the lines phase. */
	public onPhysicsTargetReached(lane: number): void {
		this._linesPhase.onPhysicsTargetReached(lane);
	}

	//Private — frame plumbing
	//-----------------------------------------------------------------------------------------------

	private _positionParticleContainer(): void {
		const sizes = this._deps.getSizes();
		const x = this._deps.context.containerX + sizes.zonePositions.systemsParticles.cx * sizes.virtualWidth;

		this._deps.stage.systemsContainer.x = x;
		this._deps.sceneProps.mouse.particleContainersX.systems = x;
	}

	private _generateBrokenGrid(): void {
		const gridSprite: PIXI.Sprite = ParallaxItemTargets.get('systems-grid');

		if (!gridSprite) {
			return;
		}

		const w = gridSprite.width;
		const h = gridSprite.height;
		ParticleGridTargets.tick(gridSprite.x, device.innerHeight / 2, w, h, this._deps.context.progress);
	}
}
